/**
 * Expo Ortho — <product-loader>: "Show more" and infinite scroll for product grids.
 * The next page is fetched as this section (?section_id=…) and its cards are appended.
 * In pagination mode there is nothing to do here; <facets-form> (facets.js) loads page links.
 * The "Show more" control is a real link, so it works without this script.
 */
(function () {
  'use strict';

  if (customElements.get('product-loader')) return;

  class ProductLoader extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode;
      if (this.mode !== 'show_more' && this.mode !== 'infinite') return;

      this.loading = false;
      this.controller = null;
      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);

      if (this.mode === 'infinite' && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
          },
          { rootMargin: '600px 0px' }
        );
        this.observeSentinel();
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this.observer) this.observer.disconnect();
      if (this.controller) this.controller.abort();
    }

    get grid() {
      return this.querySelector('[data-loader-grid]');
    }

    get nextLink() {
      return this.querySelector('[data-loader-next]');
    }

    observeSentinel() {
      const sentinel = this.querySelector('[data-loader-sentinel]');
      if (sentinel && this.observer) this.observer.observe(sentinel);
    }

    onClick(event) {
      const link = event.target.closest('[data-loader-next]');
      if (!link || !this.contains(link)) return;
      event.preventDefault();
      this.loadNext(true);
    }

    /** Loads the next page. `focusFirst` moves keyboard focus to the first new card. */
    async loadNext(focusFirst = false) {
      const link = this.nextLink;
      if (!link || this.loading) return;

      const url = link.href;
      this.loading = true;
      this.controller = new AbortController();
      this.classList.add('is-loading');
      link.classList.add('is-loading');
      link.setAttribute('aria-busy', 'true');

      try {
        const separator = url.includes('?') ? '&' : '?';
        const response = await fetch(`${url}${separator}section_id=${this.dataset.sectionId}`, {
          signal: this.controller.signal,
        });
        if (!response.ok) throw new Error(response.statusText);

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const added = this.append(doc);
        this.updateControls(doc);

        const template = this.dataset.announce || '';
        theme.utils.announce(template.replace('[number]', added.length));

        if (focusFirst && added.length) {
          const target = added[0].querySelector('a[href]:not([tabindex="-1"])');
          if (target) target.focus();
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        // The network or the section failed: fall back to the plain link.
        window.location.href = url;
        return;
      } finally {
        this.loading = false;
        this.controller = null;
        this.classList.remove('is-loading');
      }

      this.observeSentinel();
    }

    /** Appends the new grid items, skipping products already on the page. */
    append(doc) {
      const grid = this.grid;
      const incoming = doc.querySelector('[data-loader-grid]');
      if (!grid || !incoming) return [];

      const present = new Set(Array.from(grid.children, (item) => item.dataset.productId));
      const added = [];

      Array.from(incoming.children).forEach((item) => {
        const id = item.dataset.productId;
        if (id && present.has(id)) return;
        grid.append(item);
        added.push(item);
      });

      return added;
    }

    /** Replaces the status line and the "Show more" controls with the ones from the loaded page. */
    updateControls(doc) {
      const status = this.querySelector('[data-loader-status]');
      const nextStatus = doc.querySelector('[data-loader-status]');
      if (status && nextStatus) status.replaceWith(nextStatus);

      const controls = this.querySelector('[data-loader-controls]');
      const nextControls = doc.querySelector('[data-loader-controls]');
      if (!controls) return;
      if (nextControls) controls.replaceWith(nextControls);
      else controls.remove();
    }
  }

  customElements.define('product-loader', ProductLoader);
})();


/**
 * Sub-category rows on a parent collection: "Show more (N more)" loads the rest of that
 * sub-collection into the same grid (Section Rendering API), page by page, instead of leaving.
 */
(() => {
  const loading = new WeakSet();

  async function loadMore(button) {
    if (loading.has(button)) return;
    const group = button.closest('.collection-group');
    const grid = group && group.querySelector('.collection-group__grid');
    if (!grid) return;

    loading.add(button);
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    const total = Number(button.dataset.total) || 0;
    const base = new URL(button.getAttribute('href'), window.location.origin);
    base.searchParams.set('section_id', button.dataset.sectionId);
    let page = Number(button.dataset.page) || 1;
    const present = new Set(Array.from(grid.children, (item) => item.dataset.productId));
    let added = 0;

    try {
      // Walk pages until something new arrives (the first page repeats the 8 already shown).
      for (let guard = 0; guard < 10 && added === 0; guard += 1) {
        base.searchParams.set('page', String(page));
        const response = await fetch(base.toString());
        if (!response.ok) throw new Error(response.statusText);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const items = Array.from(doc.querySelectorAll('.product-grid__item[data-product-id]'));
        if (!items.length) break;
        items.forEach((item) => {
          const id = item.dataset.productId;
          if (!id || present.has(id)) return;
          present.add(id);
          grid.appendChild(document.importNode(item, true));
          added += 1;
        });
        page += 1;
        if (!doc.querySelector('[data-loader-next], .pagination__next, a[rel="next"]')) break;
      }
    } catch (error) {
      // Network or section failure: fall back to the plain link.
      window.location.href = button.getAttribute('href');
      return;
    } finally {
      loading.delete(button);
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }

    button.dataset.page = String(page);
    const shown = present.size;
    const remaining = Math.max(0, total - shown);
    if (remaining === 0 || added === 0) {
      button.closest('.collection-group__footer').remove();
    } else {
      const template = remaining === 1 ? button.dataset.labelOne : button.dataset.labelOther;
      button.textContent = (template || 'Show more ([count] more)').replace('[count]', remaining);
    }
    if (added && window.theme && theme.utils && theme.utils.announce) {
      theme.utils.announce(`${added} more products loaded`);
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-group-more]');
    if (!button) return;
    event.preventDefault();
    loadMore(button);
  });
})();
